// /js/stores/salon.js - Public Vitrina Version
import { defineStore } from 'pinia';
import Salon from '@/js/models/salon.js';
import Service from '@/js/models/service.js';
import ServiceCategory from '@/js/models/serviceCategory.js';
import { resolveSimpleIdToSalonId } from '@/js/apis/legacyResolve.js';
import { load_salon_configs } from '@/js/apis/vitrinaConfig.js';
import { isObject } from 'vuetify/lib/util/helpers.mjs';

/**
 * Salon Store - Public Vitrina Version
 * 
 * This is a simplified version of the salon store for the public vitrina.
 * It only includes functionality needed for displaying salon information
 * and services to customers.
 * 
 * Admin functions have been removed - see app-salon-tools for full version.
 */

export const useSalonStore = defineStore('salon', {
  state: () => ({
    // Current salon being viewed
    currentSalon: null,
    currentSimpleId: null,  // 5-digit customer-facing short ID (URL construction)
    salonId: null,          // 16-digit canonical salon_id (all API operations)
    
    // Services and categories
    services: [],
    serviceCategories: [],
    
    // Staff members
    staff: [],
    
    // Salon images (logo, header, gallery)
    images: [],

    // Raw public_info / settings_public from the edge config bundle. Kept
    // verbatim so fields the Salon model doesn't carry (wa, tz, ven…) stay
    // reachable without another fetch.
    publicInfo: null,
    settingsPublic: null,

    // UI state
    loading: false,
    error: null,
  }),

  getters: {
    /**
     * The vitrina is explicitly UNPUBLISHED — settings_public arrived and its
     * `ven` is false (vitrina-vue#38). STRICT `=== false`: a blob that hasn't
     * loaded yet, failed to load, or predates the `ven` field must NOT claim
     * disabled — flashing "temporalmente deshabilitado" on a config hiccup is
     * the worse wrong, and the edge + availability re-gate authoritatively.
     * This is the SPA-side belt for the paths the edge catch-all gate
     * (vitrina-gae-cdn#11) cannot cover: the 60s stamped-shell cache window
     * right after a deactivation, and the localhost salon-id dev bypass.
     */
    vitrinaDisabled: (state) => state.settingsPublic?.ven === false,

    /**
     * The salon does NOT take bookings from the storefront — settings_public
     * arrived with `voe` false (vitrina-vue#39). The vitrina stays a catalog:
     * services, prices, staff, hours all render; the booking path does not,
     * and WhatsApp carries the contact.
     *
     * `ven` is deliberately NOT consulted here — an unpublished vitrina renders
     * nothing at all one level up, in SalonLayout, so the two states never
     * compete.
     *
     * STRICT `=== false`, same rule as `vitrinaDisabled` but for the opposite
     * reason: hiding the primary CTA of a salon that DOES take bookings costs a
     * real booking, so a blob that hasn't loaded, failed, or predates the field
     * keeps the CTA. `voe` defaults to false server-side and the projection
     * fills defaults, so a real off-salon always arrives explicitly false.
     */
    onlineBookingDisabled: (state) => state.settingsPublic?.voe === false,

    /**
     * The salon lets the customer choose who attends them — `voe`'s sub-toggle
     * `vop` ("Permitir elegir profesional", vitrina-vue#40). When false the
     * booking wizard has no professional step at all and every booking goes out
     * as "Sin preferencia", which api-salon resolves to a real staff member.
     *
     * STRICT `=== true`, i.e. fail CLOSED — deliberately the OPPOSITE of
     * `onlineBookingDisabled`'s policy, and the asymmetry is the point. Hiding
     * the booking CTA costs a booking, so `voe` fails open. The picker is an
     * optional enhancement: hiding it when the flag is unknown costs the
     * customer nothing (the booking still completes), while showing it against
     * the salon's wishes walks them into staff who may have no bookable seat at
     * all (api-salon#426). `vop` also defaults to false server-side.
     */
    professionalPickEnabled: (state) => state.settingsPublic?.vop === true,

    /**
     * The salon chose to SHOW its team on the storefront — `vst` ("Mostrar
     * personal del salón", vitrina-vue#51). OPT-IN: defaults to false at
     * every plan level server-side, so STRICT === true / fail CLOSED is the
     * correct read (the vop shape, for the same reason): exposing the
     * salon's staff without an explicit choice is worse than hiding a
     * section the owner wanted — and absent means "old blob or never
     * chosen", which for an opt-in equals "off".
     */
    staffSectionEnabled: (state) => state.settingsPublic?.vst === true,

    /**
     * The salon chose the compact photo-less services listing — `vnp`
     * ("Mostrar servicios en diseño sin fotos", vitrina-vue#53). STRICT
     * === true / fail CLOSED, the vst shape: this is a cosmetic opt-in and
     * the photographic grid is the flagship default, so absent (old blob,
     * never chosen) means the default look.
     */
    photolessServicesEnabled: (state) => state.settingsPublic?.vnp === true,

    /**
     * `wa.me` link for the salon's public WhatsApp number, or null when the
     * salon published none. `wa` lives on public_info rather than the Salon
     * model (see loadSalonFromConfigs), with the model's own field as fallback.
     *
     * Lives in the store because it is now the contact path when online
     * booking is off (#39) — Home's affordances and that state must not drift
     * apart with two copies of the same builder.
     */
    whatsappLink: (state) => {
      const wa = state.publicInfo?.wa || state.currentSalon?.whatsappNumber;
      if (!wa) return null;
      const digits = String(wa).replace(/\D/g, '');
      return digits ? `https://wa.me/${digits}` : null;
    },

    /**
     * WhatsApp BOOKING mode (#55): the salon takes bookings over WhatsApp
     * instead of the online wizard. True only when ALL of:
     *   - online bookings are explicitly OFF (voe === false — the exclusivity
     *     rule: wizard, or WhatsApp booking, or pure catalog, never two);
     *   - the salon did not opt out of the WhatsApp button (`vwab` — default
     *     TRUE server-side, so absent ≠ off: `!== false`, the voe fail-open
     *     shape, because hiding the only remaining booking channel costs a
     *     real booking);
     *   - a WhatsApp number exists to receive the message.
     */
    whatsappBookingEnabled() {
      return (
        this.onlineBookingDisabled &&
        this.settingsPublic?.vwab !== false &&
        !!this.whatsappLink
      );
    },

    /**
     * L1 categories — the homologation hierarchy's top level, the ONLY level
     * the storefront offers as filter chips. Home and the wizard's step 1
     * read THIS getter so the two surfaces cannot drift (they did: the wizard
     * listed every level and exact-matched ids, so an L1 chip showed nothing
     * while Home prefix-matched fine — René 2026-08-08). Level arrives as a
     * STRING on the wire (uhlv), so the absent-parentCode leg is the
     * load-bearing test; Number() keeps the level leg honest anyway.
     */
    l1Categories() {
      const seen = new Set();
      return this.activeCategories
        .filter((c) => Number(c.level) === 1 || !c.parentCode)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        .filter((c) => !seen.has(c.id) && seen.add(c.id));
    },

    /**
     * Get active services grouped by category
     */
    servicesByCategory() {
      const grouped = {};
      this.activeCategories.forEach(category => {
        grouped[category.id] = {
          category,
          services: this.activeServices.filter(service => String(service.categoryId || '').startsWith(category.id))
            .sort((a, b) => a.sortOrder - b.sortOrder),
        };
      });
      return grouped;
    },

    /**
     * Get all active services
     */
    categoryPreferencesFor: (state) => (service) => state.serviceCategories.find(category =>
      category.id === String(service?.categoryId || '').slice(0, 6),
    ),

    isServiceBookable() {
      return service => !!service?.isActive
        && this.categoryPreferencesFor(service)?.hide_category_services !== true
        && this.categoryPreferencesFor(service)?.block_category_booking !== true;
    },

    bookableServices() {
      return this.activeServices.filter(this.isServiceBookable);
    },

    activeServices() {
      return this.services.filter(service => service.isActive
        && this.categoryPreferencesFor(service)?.hide_category_services !== true);
    },

    /**
     * Get all active categories
     */
    activeCategories: (state) => {
      return state.serviceCategories.filter(c => c.isActive && c.hide_category_services !== true
        && !state.serviceCategories.some(parent => parent.id === String(c.id).slice(0, 6) && parent.hide_category_services === true));
    },

    /**
     * Get service by ID
     */
    getServiceById: (state) => (serviceId) => {
      return state.services.find(s => s.id === serviceId);
    },

    /**
     * Get category by ID
     */
    getCategoryById: (state) => (categoryId) => {
      return state.serviceCategories.find(c => c.id === categoryId);
    },

    /**
     * Check if salon data is loaded
     */
    isSalonLoaded: (state) => {
      return state.currentSalon !== null;
    },

    /**
     * Get active staff members
     */
    activeStaff: (state) => {
      return state.staff.filter(s => s.status === 1);
    },

    /**
     * The members the booking picker may OFFER for the selected services
     * (#44) — the SPA-side mirror of the availability engine's roster
     * resolution (api-salon services/bookings/availability_service.py::
     * _resolve_roster), so the picker never shows a person the engine would
     * refuse every slot for. Two halves, like the engine's two capacity modes:
     *
     * 1. Members the blob proved unbookable are out — STRICT `bkb === false`.
     *    `bkb` is the engine's own is_service_provider answer, published by
     *    api-salon#426; it reaches a salon's blob only at its next staff-config
     *    regeneration, so ABSENT means "old blob, cannot classify" → fail OPEN
     *    and keep the member. Hiding a real professional costs a booking —
     *    the same strict-comparison shape as vitrinaDisabled (#38) and
     *    onlineBookingDisabled (#39).
     * 2. CONFIGURED-mode mirror: members whose populated `asvc` covers EVERY
     *    selected service ARE the engine's roster when any exist — nobody else
     *    can be granted a slot for this request, so nobody else is offered.
     *    When no one covers, the engine serves the request from the shared-
     *    capacity provider pool, which is exactly the set from (1).
     *
     * Computing (2) inside the (1) pool matches the engine's staff-wide scan:
     * a populated asvc makes is_service_provider — and thus bkb — true, and
     * activeStaff applies the same ACTIVE gate the policy self-checks. The one
     * divergence is deliberate and defensive: a member explicitly bkb:false
     * can never be resurrected by their asvc here (the producer derives bkb
     * FROM asvc, so the combination shouldn't exist on the wire).
     *
     * The Equipo section deliberately does NOT use this getter: a salon
     * presents its whole team, owner included (René, 2026-08-04). bkb
     * separates "offer as a bookable seat" from "show as staff".
     */
    bookableStaffFor() {
      return (serviceIds = []) => {
        const pool = this.activeStaff.filter((m) => m.bookable !== false);
        const ids = (serviceIds ?? []).filter(Boolean);
        if (ids.length === 0) return pool;
        const covering = pool.filter(
          (m) =>
            (m.assignedServices?.length ?? 0) > 0 &&
            ids.every((id) => m.assignedServices.includes(id))
        );
        return covering.length ? covering : pool;
      };
    },

    /**
     * Get staff member by ID
     */
    getStaffById: (state) => (staffId) => {
      return state.staff.find(s => s.id === staffId);
    },

    /**
     * Get salon basic info.
     *
     * Reads the Salon MODEL's field names (models/salon.js). It used to read
     * a shape nothing produces — `name`, `phone`, `logo`, `coverImage`,
     * `socialMedia`, plus `city`/`state`/`postal_code` as if they were on the
     * salon rather than on its Address — so 7 of 13 fields came back
     * undefined for every caller. It stayed unnoticed because nothing
     * consumed the getter.
     *
     * `socialMedia` is dropped rather than mapped: the model has no such
     * field and inventing one here would hide that, exactly the way the old
     * keys did.
     */
    salonInfo: (state) => {
      if (!state.currentSalon) return null;

      const address = state.currentSalon.address;

      return {
        name: state.currentSalon.salonName,
        description: state.currentSalon.description,
        phone: state.currentSalon.phoneNumber,
        email: state.currentSalon.email,
        address,
        city: address?.city ?? null,
        state: address?.state ?? null,
        country: state.currentSalon.country,
        postal_code: address?.postal_code ?? null,
        logo: state.currentSalon.primaryLogo,
        coverImage: state.currentSalon.backgroundImage,
        businessHours: state.currentSalon.businessHours,
      };
    },

    /**
     * Get salon logo URL from images config
     */
    salonLogo: (state) => {
      const logo = state.images.find(img => img.tp === 'logo');
      return logo?.surl || null;
    },

    /**
     * Get salon header/banner URL from images config
     */
    salonBanner: (state) => {
      const header = state.images.find(img => img.tp === 'header');
      return header?.surl || null;
    },

    /**
     * Get gallery image URLs from images config
     */
    galleryImages: (state) => {
      return state.images
        .filter(img => img.tp === 'gallery')
        .map(img => img.surl);
    },
  },

  actions: {
    /**
     * Load public salon data by simple ID
     * This is the main entry point for the vitrina
     */
    async loadSalonDetailsBySimpleId(simpleId) {
      // Legacy /salon/:slug/:simpleId entry point (#31). One edge call
      // resolves the canonical salon id, then the CLEAN-SLUG path takes over
      // — same config bundle, same rendering, zero direct runapis reads.
      // This method used to fan out to five runapis endpoints; that made the
      // deprecated route the storefront's last standing counter-example to
      // "all vitrina traffic goes through the edge", and it bypassed the
      // edge's gate — a salon that turned its vitrina off stayed readable
      // through the old URL.
      this.loading = true;
      this.error = null;

      try {
        const salonId = await resolveSimpleIdToSalonId(simpleId);
        if (!salonId) {
          throw new Error('Salon not found');
        }

        // Stamp the globals the clean-slug boot would have set, so every
        // downstream consumer (telemetry, __loadConfig, availability) sees
        // the same world regardless of which URL form brought us here.
        window.__SALON_ID__ = salonId;

        return await this.loadSalonFromConfigs(salonId);
      } catch (error) {
        console.error('[salon store] Error loading salon (legacy route):', error);
        this.error = error.message || 'Error loading salon data';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Load the salon from the vitrina edge config bundle (two-tier,
     * content-addressed — see apis/vitrinaConfig.js). This is the clean-slug
     * entry point: the salon id was already resolved at boot (edge-injected
     * meta, or the localhost localStorage override); no simpleId involved.
     */
    async loadSalonFromConfigs(salonId) {
      this.loading = true;
      this.error = null;

      try {
        console.log(`[salon store] Loading salon from edge configs: ${salonId}`);
        const bundle = await load_salon_configs(salonId);
        const configs = bundle.configs || {};

        const public_info = configs.public_info?.data || {};
        this.publicInfo = public_info;
        this.settingsPublic = configs.settings_public?.data || null;

        // public_info carries the presentation fields with the same wire
        // aliases the Salon model already maps (snm/desc/ph/em/bhrs/bt/ad).
        // vth (visual theme) uses a different shape than the model expects, so
        // it's adapted; wa/tz stay reachable via state.publicInfo.
        const { vth, wa, tz, ...salon_aliased } = public_info;
        this.currentSalon = Salon.fromAlias({
          ...salon_aliased,
          sid: String(salonId),
          vtheme: this._themeFromPublicInfo(vth),
        });
        this.salonId = String(salonId);
        this.currentSimpleId = null;

        // cached_configs blobs are aliased arrays — the models map them 1:1.
        const services_data = configs.services?.data;
        this.services = Array.isArray(services_data)
          ? services_data.map(s => Service.fromAlias(s))
          : [];
        const categories_data = configs.categories?.data;
        this.serviceCategories = Array.isArray(categories_data)
          ? categories_data.map(c => ServiceCategory.fromAlias(c))
          : [];

        // `staff_public` is the consumer projection (api-salon#484). Its admin
        // twin `staff_salon` carries the RBAC bitmask and inactive rows and is not
        // even served by this edge — the storefront must never reach for it.
        //
        // Falls back to the legacy `staff` key while salons still carry pre-split
        // blobs: a salon's projections appear only at its next regeneration, so
        // without this its team would vanish from the storefront until then.
        this._processStaffData(
          configs.staff_public?.data || configs.staff?.data || []
        );

        const images_data = configs.images?.data;
        this.images = Array.isArray(images_data) ? images_data : [];

        console.log(
          `[salon store] Salon loaded from configs: ${this.services.length} services, ` +
            `${this.serviceCategories.length} categories, ${this.staff.length} staff, ` +
            `${this.images.length} images`
        );
        return this.currentSalon;
      } catch (error) {
        console.error('[salon store] Error loading salon from configs:', error);
        this.error = error.message || 'Error loading salon data';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Adapt public_info's vth ({style_name, name, colors:{primary…}}) to the
     * {themeId: themeData} shape the Salon model / salonThemeManager expect.
     * Emits Theme wire aliases (pc/sc/bc/tc/ac) so Salon's constructor
     * instantiates it through Theme.fromAlias like any other theme.
     * @private
     */
    _themeFromPublicInfo(vth) {
      if (!vth || typeof vth !== 'object') return null;
      const colors = vth.colors || {};
      const theme_id = vth.style_name || 'salon';
      return {
        [theme_id]: {
          nm: vth.name || theme_id,
          pc: colors.primary,
          sc: colors.secondary,
          bc: colors.background,
          tc: colors.text,
          ac: colors.accent,
        },
      };
    },

    /**
     * Process and store staff data
     * @private
     */
    _processStaffData(staffData) {
      if (!staffData || !Array.isArray(staffData)) {
        this.staff = [];
        return;
      }
      
      // Map staff data to frontend format (supports both aliased and full field names)
      this.staff = staffData.map(s => ({
        id: s.id,
        name: s.un || s.name || `${s.fn || s.first_name || ''} ${s.ln || s.last_name || ''}`.trim(),
        firstName: s.fn || s.first_name,
        lastName: s.ln || s.last_name,
        email: s.em || s.email,
        phone: s.ph || s.phone_number,
        // `tl`, NOT `title`: the public staff blob emits the short alias
        // (repositories/salon_cached_config.py). Reading `s.title` left every
        // professional untitled, which is why `specialty` below fell through to
        // the generic "Profesional" for the whole team.
        title: s.tl || s.title,
        // `pph` is profile_photo; `pp` is permission_profiles, an INTEGER
        // (models/users.py). Reading `pp` here put an RBAC id where a URL
        // belongs — the team section and booking step 2 rendered
        // <img src="16">, so no staff photo has ever loaded on the vitrina,
        // and an internal permission id was being written into the DOM.
        profilePhoto: s.pph || s.profile_photo,
        status: s.st ?? s.status ?? 1,
        // `bkb` — can a customer actually book this member (#44)? The
        // availability engine's own is_service_provider answer, published by
        // the public blob since api-salon#426; absent on blobs generated
        // before it. `??`, never `||`: explicit false is the only value that
        // means anything and a truthiness default would erase it.
        bookable: s.bkb ?? s.bookable,
        hireDate: s.hdt || s.hire_date,
        totalBookings: s.tb ?? s.total_bookings ?? 0,
        lastBooking: s.last_booking,
        assignedServices: s.asvc || [],
        // Frontend display fields
        image: s.pph || s.profile_photo,
        // The salon's own title when it set one, and NOTHING when it didn't
        // (#41). The old `|| 'Profesional'` fallback labelled a CASHIER and a
        // MANAGER "Profesional" on Glam Studio's public storefront — a claim
        // about a person that the salon never made.
        specialty: s.tl || s.title || null
        // NO placeholder display fields (#41). `rating: 5.0`, `reviews: 0`,
        // `availableSlots: 10`, `experience: ''` and `certifications: []` were
        // invented here and reached the customer: booking step 2 rendered
        // "5 (0)" and "10 espacios" under every professional, including members
        // with zero bookable slots. A fabricated 5-star rating is not ours to
        // publish on a salon's behalf, and it broke the rule this repo states in
        // Home.vue: real ratings only, every rating surface hides until reviews
        // exist. Every consumer guards on presence, so those surfaces now stay
        // hidden until real data backs them.
      }));
      
      console.log(`[salon store] Processed ${this.staff.length} staff members`);
    },

    /**
     * Process and store salon data
     * @private
     */
    async _processSalonData(salonData) {
      // Convert aliased wire data to full field names via Salon.fromAlias()
      // where needed. The model's alias map now correctly splits sid → salonId
      // (16-digit, canonical) and siid → simpleId (5-digit, URL-only).
      let processedSalonData;
      if (salonData.snm || salonData.cc || salonData.ad || salonData.sid) {
        console.log('[salon store] Data has aliased fields, using Salon.fromAlias()');
        processedSalonData = Salon.fromAlias(salonData);
      } else if (salonData instanceof Salon) {
        console.log('[salon store] Data is already a Salon instance');
        processedSalonData = salonData;
      } else {
        console.log('[salon store] Data has full field names, using new Salon()');
        processedSalonData = new Salon(salonData);
      }

      this.currentSalon = processedSalonData;
      this.currentSimpleId = processedSalonData.simpleId;
      this.salonId = processedSalonData.salonId;
      console.log(`[salon store] salonId (16-digit): ${this.salonId}, currentSimpleId (5-digit): ${this.currentSimpleId}`);

      // Process services - use fromAlias if data has aliases
      if (salonData.services && isObject(salonData.services)) {
        const servicesArray = Object.entries(salonData.services).map(([id, serviceData]) => {
          // Check if service data has aliases and use fromAlias accordingly
          if (serviceData.nm || serviceData.cid || serviceData.desc) {
            // Service data has aliases, use fromAlias
            return Service.fromAlias({ id: id, ...serviceData });
          } else {
            // Service data has full field names, use constructor
            return new Service({
              id: id,
              categoryId: serviceData.categoryId,
              name: serviceData.name,
              description: serviceData.description,
              duration: serviceData.duration,
              price: serviceData.price,
              currency: serviceData.currency || 'MXN',
              sortOrder: serviceData.sortOrder || 0,
              isActive: serviceData.isActive !== undefined ? serviceData.isActive : true,
              isPopular: serviceData.isPopular || false,
              image: serviceData.image,
              gallery: serviceData.gallery || [],
              minPrice: serviceData.minPrice,
              maxPrice: serviceData.maxPrice,
              pricingType: serviceData.pricingType || 'fixed',
            });
          }
        });
        
        this.services = servicesArray;
      } else {
        this.services = [];
      }

      // Process categories - use fromAlias if data has aliases
      if (salonData.serviceCategories && isObject(salonData.serviceCategories)) {
        const categoriesArray = Object.entries(salonData.serviceCategories).map(([id, categoryData]) => {
          // Check if category data has aliases and use fromAlias accordingly
          if (categoryData.nm || categoryData.desc || categoryData.ic) {
            // Category data has aliases, use fromAlias
            return ServiceCategory.fromAlias({ id: id, ...categoryData });
          } else {
            // Category data has full field names, use constructor
            return new ServiceCategory({
              id: id,
              name: categoryData.name,
              description: categoryData.description,
              sortOrder: categoryData.sortOrder || 0,
              isActive: categoryData.isActive !== undefined ? categoryData.isActive : true,
              icon: categoryData.icon,
              image: categoryData.image,
            });
          }
        });
        
        this.serviceCategories = categoriesArray;
      } else {
        this.serviceCategories = [];
      }

      console.log(`[salon store] Processed ${this.services.length} services and ${this.serviceCategories.length} categories`);
    },

    /**
     * Clear salon data
     */
    clearSalon() {
      this.currentSalon = null;
      this.currentSimpleId = null;
      this.salonId = null;
      this.services = [];
      this.serviceCategories = [];
      this.staff = [];
      this.images = [];
      this.error = null;
    },

    /**
     * Refresh current salon data
     */
    async refreshSalon() {
      if (!this.currentSimpleId) {
        console.warn('[salon store] No salon loaded to refresh');
        return;
      }

      await this.loadSalonDetailsBySimpleId(this.currentSimpleId);
    },
  },
});
